import { IS_OPERATOR, makeStringifySafeMutations, createActionsProxy, ORIGINAL_ACTIONS } from './utils';
import { EventType } from './internalTypes';
import { safeValue } from './Devtools';
export function operatorStarted(type, arg, context) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  var name = typeof arg === 'function' ? arg.displayName || arg.name : String(arg);
  context.execution.emit(EventType.OPERATOR_START, Object.assign({}, context.execution, {
    name: name,
    type: type
  }));
}
export function operatorStopped(context, value) {
  var details = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  if (process.env.NODE_ENV === 'production') {
    if (value instanceof Promise) {
      context.execution.emit(EventType.OPERATOR_ASYNC, Object.assign({}, context.execution, {
        isAsync: true
      }));
    }

    return;
  }

  var evaluatedDetails = {
    error: details.error ? details.error.message : undefined,
    isIntercepted: Boolean(details.isIntercepted),
    isSkipped: Boolean(details.isSkipped)
  };

  if (value instanceof Promise) {
    value.then(function (promiseValue) {
      context.execution.isRunning = false;
      context.execution.emit(EventType.OPERATOR_END, Object.assign({}, context.execution, {
        result: safeValue(promiseValue),
        isAsync: true
      }, evaluatedDetails));
    }).catch(function () {// Make sure an error does not cause uncaught
    });
  } else {
    context.execution.isRunning = false;
    context.execution.emit(EventType.OPERATOR_END, Object.assign({}, context.execution, {
      result: safeValue(value),
      isAsync: false
    }, evaluatedDetails));
  }
}
export function createContext(context, value, path) {
  if (process.env.NODE_ENV === 'production') {
    return Object.assign({}, context, {
      value: value
    });
  }

  var newExecution = Object.assign({}, context.execution, {
    operatorId: context.execution.getNextOperatorId(),
    path: path || context.execution.path
  });
  return Object.assign({}, context, {
    actions: createActionsProxy(context.actions[ORIGINAL_ACTIONS] || context.actions, function (action) {
      return function (value) {
        return action(value, newExecution.isRunning ? newExecution : null);
      };
    }),
    value: value,
    execution: newExecution,
    effects: context.execution.trackEffects(newExecution)
  });
}
export function createNextPath(next) {
  if (process.env.NODE_ENV === 'production') {
    return next;
  }

  return function (err, context) {
    var newContext = Object.assign({}, context, {
      execution: Object.assign({}, context.execution, {
        path: context.execution.path.slice(0, context.execution.path.length - 1)
      })
    });
    if (err) next(err, newContext);else next(null, newContext);
  };
}
export function createOperator(type, name, cb) {
  var operator = function operator(err, context, next, _final) {
    operatorStarted(type, name, context);
    var nextIsCalled = false;

    try {
      cb(err, {
        state: context.state,
        effects: context.effects,
        actions: context.actions
      }, context.value, function (err, value) {
        var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

        function run(err, value) {
          if (options.path) {
            var newContext = createContext(context, value, context.execution.path && context.execution.path.concat(options.path.name));
            var nextWithPath = createNextPath(next);
            options.path.operator(err, newContext, function () {
              for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = arguments[_key];
              }

              operatorStopped(context, args[1].value);
              nextWithPath.apply(void 0, args);
            });
          } else {
            operatorStopped(context, err || value, {
              isSkipped: err ? true : options.isSkipped
            });
            next(err, createContext(context, value));
          }
        }

        if (value && value instanceof Promise) {
          value.then(function (promiseValue) {
            return run(err, promiseValue);
          }).catch(function (promiseError) {
            return run(promiseError, promiseError);
          });
        } else {
          nextIsCalled = true;
          run(err, value);
        }
      }, function (err, value) {
        nextIsCalled = true;
        operatorStopped(context, err || value, {
          isSkipped: Boolean(err),
          isIntercepted: !err
        });

        _final(err, createContext(context, value));
      });
    } catch (error) {
      nextIsCalled = true;
      operatorStopped(context, context.value, {
        error: error
      });
      next(error, createContext(context, context.value));
    }

    if (!nextIsCalled) {
      context.execution.emit(EventType.OPERATOR_ASYNC, Object.assign({}, context.execution, {
        isAsync: true
      }));
    }
  };

  operator[IS_OPERATOR] = true;
  return operator;
}
export function createMutationOperator(type, name, cb) {
  var operator = function operator(err, context, next, _final2) {
    operatorStarted(type, name, context);
    var mutationTree = context.execution.getMutationTree();

    if (!(process.env.NODE_ENV === 'production')) {
      mutationTree.onMutation(function (mutation) {
        context.execution.emit(EventType.MUTATIONS, Object.assign({}, context.execution, {
          mutations: makeStringifySafeMutations([mutation])
        }));
      });
    }

    var nextIsCalled = false;

    try {
      cb(err, {
        state: mutationTree.state,
        effects: context.effects,
        actions: context.actions
      }, process.env.NODE_ENV === 'production' ? context.value : context.execution.scopeValue(context.value, mutationTree), function (err, value) {
        var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

        function run(err, value) {
          operatorStopped(context, err || value, {
            isSkipped: err ? true : options.isSkipped
          });
          mutationTree.dispose();
          next(err, createContext(context, value));
        }

        if (value && value instanceof Promise) {
          value.then(function (promiseValue) {
            return run(err, promiseValue);
          }).catch(function (promiseError) {
            return run(promiseError, promiseError);
          });
        } else {
          nextIsCalled = true;
          run(err, value);
        }
      }, function (err, value) {
        nextIsCalled = true;
        operatorStopped(context, err || value, {
          isSkipped: Boolean(err),
          isIntercepted: !err
        });

        _final2(err, createContext(context, value));
      });

      if (!(process.env.NODE_ENV === 'production')) {
        var pendingFlush;
        mutationTree.onMutation(function () {
          if (pendingFlush) {
            clearTimeout(pendingFlush);
          }

          pendingFlush = setTimeout(function () {
            var flushData = context.execution.flush(true);

            if (flushData.mutations.length) {
              context.execution.send({
                type: 'flush',
                data: Object.assign({}, context.execution, flushData, {
                  mutations: makeStringifySafeMutations(flushData.mutations)
                })
              });
            }
          });
        });
      }
    } catch (error) {
      nextIsCalled = true;
      operatorStopped(context, context.value, {
        error: error
      });
      next(error, createContext(context, context.value));
    }

    if (!nextIsCalled) {
      context.execution.emit(EventType.OPERATOR_ASYNC, Object.assign({}, context.execution, {
        isAsync: true
      }));
    }
  };

  operator[IS_OPERATOR] = true;
  return operator;
}